import { supabase } from '../../../lib/supabase'

/** Shape returned by `list_colorful_weekly_winners`. */
export interface ColorfulWeeklyWinnerRow {
  event_id: string
  week_start: string
  week_end: string
  author_id: string
  author_name: string
  total_score: number
  /** Players who actually picked a colour that week. Excludes the
   *  empty entry rows created when someone merely opens the screen. */
  participant_count: number
  /** Every top scorer, joined with " & ". Weeks tie routinely now
   *  that a tied round scores nothing, so `author_name` alone would
   *  credit one player for a shared win. */
  winner_names: string
  winner_count: number
}

export interface ColorfulWeeklyEventRow {
  id: string
  week_start: string
  week_end: string
  subjects: string[]
  is_final: boolean
  created_at: string
}

export interface ColorfulWeeklyEntryRow {
  id: string
  event_id: string
  profile_id: string
  picks: (string | null)[]
  total_score: number
  is_final: boolean
  created_at: string
  updated_at: string
}

/**
 * Returns the current week's event. Creates it on first call and
 * finalizes the prior week as a side-effect (matches IET weekly).
 *
 * (We used to wrap supabase.rpc through a typed-cast helper, but that
 * trick unbinds `this` and intermittently throws "undefined is not an
 * object (evaluating 'this.rest')" on newer @supabase/supabase-js.
 * Direct calls work for both authenticated and anonymous sessions.)
 */
export async function getOrCreateColorfulWeekly(
  weekStart: string,
): Promise<ColorfulWeeklyEventRow> {
  const { data, error } = await supabase.rpc('get_or_create_colorful_weekly_event', {
    p_week_start: weekStart,
  })
  if (error) throw error
  return data as ColorfulWeeklyEventRow
}

/** Returns the prior week's finalized event (for the "Last Week" card). */
export async function fetchLastFinalizedColorfulWeekly(
  beforeWeekStart: string,
): Promise<ColorfulWeeklyEventRow | null> {
  const { data, error } = await supabase
    .from('colorful_weekly_events')
    .select('*')
    .eq('is_final', true)
    .lt('week_start', beforeWeekStart)
    .order('week_start', { ascending: false })
    .limit(1)
    .maybeSingle()
  if (error) throw error
  return (data as ColorfulWeeklyEventRow | null) ?? null
}

/** Ensures the caller has an entry row for this event. */
export async function ensureMyColorfulWeeklyEntry(
  eventId: string,
): Promise<ColorfulWeeklyEntryRow> {
  const { data, error } = await supabase.rpc('ensure_my_colorful_weekly_entry', {
    p_event_id: eventId,
  })
  if (error) throw error
  return data as ColorfulWeeklyEntryRow
}

/** Writes a single round's pick (0-indexed). */
export async function saveColorfulWeeklyPick(
  entryId: string, round: number, colorId: string,
): Promise<ColorfulWeeklyEntryRow> {
  const { data, error } = await supabase.rpc('save_colorful_weekly_pick', {
    p_entry_id: entryId, p_round: round, p_color_id: colorId,
  })
  if (error) throw error
  return data as ColorfulWeeklyEntryRow
}

/** Top scorer per finalized week. `exclude` filters out a specific week_start. */
export async function fetchColorfulWeeklyWinners(
  excludeWeekStart?: string,
): Promise<ColorfulWeeklyWinnerRow[]> {
  const { data, error } = await supabase.rpc('list_colorful_weekly_winners', {
    // Effectively uncapped, so the archive can never fall behind the
    // trophy shelf. See the note in fetchWeeklyWinners.
    p_exclude: excludeWeekStart ?? null, p_limit: 260,
  })
  if (error) throw error
  return Array.isArray(data) ? data as ColorfulWeeklyWinnerRow[] : []
}

/** All entries for a finalized event, newest first. For the winners screen. */
export async function fetchColorfulWeeklyEntries(
  eventId: string,
): Promise<Array<ColorfulWeeklyEntryRow & { author_name: string | null; author_avatar: string | null }>> {
  const { data, error } = await supabase
    .from('colorful_weekly_entries')
    .select('*, profiles!inner(display_name, avatar_url)')
    .eq('event_id', eventId)
    .order('total_score', { ascending: false })
  if (error) throw error
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (data ?? []).map((row: any) => ({
    ...row,
    author_name: row.profiles?.display_name ?? null,
    author_avatar: row.profiles?.avatar_url ?? null,
  }))
}

/**
 * Current-week Monday in ISO (YYYY-MM-DD). Matches the date math
 * used by the IET weekly so the two stay in lock-step.
 */
export function thisMondayISO(now: Date = new Date()): string {
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
  const dow = d.getUTCDay() // 0 = Sunday
  const daysSinceMonday = (dow + 6) % 7
  d.setUTCDate(d.getUTCDate() - daysSinceMonday)
  return d.toISOString().slice(0, 10)
}

export function daysUntilNextMonday(weekStart: string): number {
  const start = new Date(weekStart + 'T00:00:00Z')
  const nextMonday = new Date(start)
  nextMonday.setUTCDate(start.getUTCDate() + 7)
  const now = new Date()
  const diffMs = nextMonday.getTime() - now.getTime()
  return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)))
}

/** "Week of Jun 29 – Jul 5" — which week a submission lands in. UTC on
 *  purpose: weeks flip at midnight UTC Sunday→Monday, and showing the range
 *  keeps a Monday-morning session from being mistaken for last week. */
export function weekRangeLabel(weekStart: string): string {
  const start = new Date(weekStart + 'T00:00:00Z')
  const end = new Date(start); end.setUTCDate(end.getUTCDate() + 6)
  const fmt = (d: Date) => d.toLocaleDateString('en-US', {
    timeZone: 'UTC', month: 'short', day: 'numeric',
  })
  return `Week of ${fmt(start)} – ${fmt(end)}`
}

export function weekLabel(weekStart: string): string {
  const d = new Date(weekStart + 'T00:00:00Z')
  return d.toLocaleDateString('en-US', {
    timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric',
  })
}

/** Archive label — week's final (Sunday) date in caps, e.g. "APR 26 2026". */
export function colorfulArchiveDateLabel(weekStart: string): string {
  const start = new Date(weekStart + 'T00:00:00Z')
  const end = new Date(start); end.setUTCDate(end.getUTCDate() + 6)
  return end.toLocaleDateString('en-US', {
    timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric',
  }).replace(',', '').toUpperCase()
}
